/* ============================================================================
   Manager credential — verified SERVER-side. The passphrase hash never syncs
   to clients any more; signing in exchanges the passphrase for a bearer token
   that destructive calls (project deletion, studio-team writes, passphrase
   changes) carry as X-Manager-Token.
   ========================================================================== */

const TKEY = "atelier.mgr.token.v1";   // name predates member tokens — it now holds ANY session token

export function getManagerToken(): string | null {
  try { return localStorage.getItem(TKEY); } catch { return null; }
}
export function setManagerToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TKEY, t);
    else localStorage.removeItem(TKEY);
  } catch { /* ignore */ }
}

/** Header object for API calls — empty when signed out. */
export function authHeader(): Record<string, string> {
  const t = getManagerToken();
  return t ? { "X-Session-Token": t } : {};
}

/** Exchange an access code for a server-verified session + token.
    null = the server checked and found no match; "offline" = couldn't check. */
export async function serverCodeLogin(code: string): Promise<{ session: unknown; token: string } | null | "offline"> {
  try {
    const res = await fetch("/api/auth/code", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (res.status === 401) return null;
    if (res.status === 429) return null;   // throttled — treat as no match rather than falling back
    const out = await jsonOf(res);
    if (res.ok && out?.ok && out.session && typeof out.token === "string") {
      setManagerToken(out.token);
      return { session: out.session, token: out.token };
    }
    return "offline";
  } catch {
    return "offline";
  }
}

type AuthResult = "ok" | "bad" | "unset" | "offline";

async function jsonOf(res: Response): Promise<Record<string, unknown> | null> {
  try { return await res.json() as Record<string, unknown>; } catch { return null; }
}

/** Verify the passphrase against the server. "offline" means the API couldn't
    be reached (or isn't a worker — plain local dev), never a wrong passphrase. */
export async function serverManagerLogin(passphrase: string): Promise<AuthResult> {
  try {
    const res = await fetch("/api/auth/manager", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ passphrase }),
    });
    if (res.status === 401 || res.status === 429) return "bad";
    if (res.status === 409) return "unset";   // server live, no passphrase configured
    const out = await jsonOf(res);
    if (res.ok && out?.ok && typeof out.token === "string") {
      setManagerToken(out.token);
      return "ok";
    }
    return "offline";
  } catch {
    return "offline";
  }
}

/** Set or change the passphrase. Once one exists the server insists on the
    current passphrase or a live manager session. */
export async function serverSetPassphrase(next: string, current?: string): Promise<{ ok: boolean; reason?: "unauthorized" | "offline" }> {
  try {
    const res = await fetch("/api/auth/passphrase", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ next, current: current || undefined, token: getManagerToken() ?? undefined }),
    });
    if (res.status === 401 || res.status === 429) return { ok: false, reason: "unauthorized" };
    const out = await jsonOf(res);
    if (res.ok && out?.ok) {
      if (typeof out.token === "string") setManagerToken(out.token);
      return { ok: true };
    }
    return { ok: false, reason: "offline" };
  } catch {
    return { ok: false, reason: "offline" };
  }
}

/** Clear the passphrase (revert to default codes). Same proof required. */
export async function serverClearPassphrase(current?: string): Promise<{ ok: boolean; reason?: "unauthorized" | "offline" }> {
  try {
    const res = await fetch("/api/auth/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ current: current || undefined, token: getManagerToken() ?? undefined }),
    });
    if (res.status === 401 || res.status === 429) return { ok: false, reason: "unauthorized" };
    const out = await jsonOf(res);
    if (res.ok && out?.ok) return { ok: true };
    return { ok: false, reason: "offline" };
  } catch {
    return { ok: false, reason: "offline" };
  }
}
